import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

export default function Acessos() {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loadError, setLoadError] = useState("");
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    if (!isAdmin) return;
    return api.get("/usuarios").then((r) => {
      setUsuarios([...r.data].sort((a, b) => Number(b.access_status === "pending") - Number(a.access_status === "pending")));
      setLoadError("");
    }).catch((err) => setLoadError(apiError(err)));
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const alternar = async (u, valor) => {
    setBusy(u.id);
    try {
      await api.patch(`/usuarios/${u.id}`, { fin_access: valor });
      toast.success(valor ? `${u.name} autorizado ao financeiro/comercial` : `Acesso de ${u.name} removido`);
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const aprovar = async (u) => {
    setBusy(u.id);
    try {
      await api.patch(`/usuarios/${u.id}`, { approve: true });
      toast.success(`${u.name} pode entrar na operação do estúdio. O financeiro continua separado.`);
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  if (!isAdmin)
    return (
      <div className="studio-card p-12 text-center max-w-lg mx-auto mt-10" data-testid="acessos-restrito">
        <Lock size={32} className="text-[#8CC63F] mx-auto mb-5" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-[#F4F4F0]">Apenas <span className="gold-italic">administradores</span></h2>
        <p className="text-sm text-[#A1A1AA] mt-3">A gestão de acessos é exclusiva do perfil administrador.</p>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="acessos-page">
      <p className="text-sm text-[#A1A1AA] max-w-xl">Aprove novos cadastros para liberar clientes, produção e calendário. As contas atuais da equipe mantêm seu acesso. O financeiro e o comercial têm autorização separada.</p>
      {loadError && <div role="alert" className="text-sm text-[#F43F5E]">{loadError} <button type="button" onClick={load} className="underline">Tentar novamente</button></div>}

      <div className="space-y-3">
        {usuarios.map((u) => (
          <div key={u.id} data-testid={`usuario-row-${u.id}`} className="studio-card px-5 py-4 flex flex-wrap items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#8CC63F]/12 border border-[#8CC63F]/30 flex items-center justify-center font-serif text-[#8CC63F]">{u.name?.[0] || "?"}</div>
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm font-medium text-[#E4E4E0]">{u.name}</p>
              <p className="caption-label mt-0.5 normal-case tracking-normal">{u.email}</p>
            </div>
            <span className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${u.role === "admin" ? "text-[#8CC63F] border-[#8CC63F]/30 bg-[#8CC63F]/10" : "text-[#71717A] border-[#27272A]"}`}>
              {u.access_status === "pending" ? "aguardando aprovação" : u.role === "admin" ? "admin" : "equipe"}
            </span>
            {u.access_status === "pending" ? (
              <Button disabled={busy !== null} onClick={() => aprovar(u)} data-testid={`usuario-approve-${u.id}`} className="bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full">{busy === u.id ? "Aprovando…" : "Aprovar acesso à operação"}</Button>
            ) : <div className="flex items-center gap-3">
              <span className="caption-label normal-case tracking-normal">Financeiro + Comercial</span>
              {u.role === "admin" ? (
                <ShieldCheck size={18} className="text-[#8CC63F]" data-testid={`usuario-admin-badge-${u.id}`} />
              ) : (
                <Switch aria-label={`Acesso financeiro e comercial de ${u.name}`} disabled={busy !== null} data-testid={`usuario-fin-toggle-${u.id}`} checked={u.fin_access} onCheckedChange={(v) => alternar(u, v)} />
              )}
            </div>}
          </div>
        ))}
      </div>
    </div>
  );
}
